import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { MissingItem } from '../state';

import { IconedLabel } from '@/components/ui';

/**
 * What's missing (brief §3): a chip — "Complete", or "2 to add" — and, while
 * something is missing, a sentence and a link per item that opens the card it
 * is added in. Drawn as Web's `ui/RecommendationCard`: a fog-10 box padded
 * 24 holding two groups 16 apart — the status line over the message, then
 * NEXT STEP (an arrow and plain uppercase label, no tracking) over a contained
 * secondary button per item.
 */
export type WhatsMissingProps = {
  missing: MissingItem[];
  onGo: (item: MissingItem) => void;
};

export const WhatsMissing = ({ missing, onGo }: WhatsMissingProps) => {
  const { t, i18n } = useTranslation('pmDetails');
  const count = missing.length;
  const nouns = new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
    missing.map((item) => t(`panel.missing.${item}.Noun`)),
  );
  return (
    <Stack
      data-automation-id="details-whats-missing"
      sx={{
        gap: 2,
        p: 3,
        bgcolor: 'var(--color-fog-10)',
        borderRadius: 'var(--border-radius-x-large)',
      }}
    >
      <Stack>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Stack
            direction="row"
            data-automation-id="details-missing-chip"
            sx={{
              alignItems: 'center',
              gap: 0.5,
              height: 24,
              px: 1,
              borderRadius: 'var(--border-radius-pill)',
              bgcolor: count > 0 ? 'var(--color-warning-10)' : 'var(--color-success-10)',
              color: count > 0 ? 'var(--fg-1)' : 'var(--color-success-100)',
            }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: 'var(--border-radius-pill)',
                bgcolor: count > 0 ? 'var(--color-warning-80)' : 'var(--color-success-80)',
              }}
            />
            <Typography variant="labelMono" component="span" sx={{ color: 'inherit' }}>
              {count > 0 ? t('panel.ToAdd', { count }) : t('panel.Complete')}
            </Typography>
          </Stack>
          {count > 0 ? (
            <Typography variant="label" component="span" uppercase>
              {t('panel.BeforeGoLive')}
            </Typography>
          ) : null}
        </Stack>
        <Typography variant="h4" component="p" data-automation-id="details-missing-message">
          {count > 0
            ? t('panel.MissingMessage', { count, list: nouns })
            : t('panel.NothingMissing')}
        </Typography>
      </Stack>
      {count > 0 ? (
        <Stack sx={{ gap: 1, alignItems: 'flex-start' }}>
          <IconedLabel icon="arrow_forward" uppercase>
            {t('panel.NextStep')}
          </IconedLabel>
          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
            {missing.map((item) => (
              <Button
                key={item}
                variant="contained"
                color="secondary"
                size="small"
                onClick={() => onGo(item)}
                data-automation-id={`details-missing-go-${item}`}
              >
                {t(`panel.missing.${item}.Action`)}
              </Button>
            ))}
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
};
